import { z } from 'zod';

/**
 * Reject extra properties passed by callers, with explicit `$`-prefix
 * protection — `$current_url`, `$session_id`, `$browser`, `$feature_flag_*`,
 * etc. are PostHog auto-capture keys; overriding them silently corrupts
 * session / replay / device data. The `.strict()` call catches arbitrary
 * extras; the refinement is the explicit safety net for `$`-prefixed keys
 * that would slip past `.strict()` if a registered schema ever included
 * one (it shouldn't).
 */
export const NoReservedKeys = <T extends z.ZodRawShape>(schema: z.ZodObject<T>) =>
  schema.strict().refine((props) => Object.keys(props).every((k) => !k.startsWith('$')), {
    message: 'Property keys must not start with `$` (reserved by PostHog).',
  });

export type EventKind = 'conversion' | 'ux';

export const signup_completed_schema = NoReservedKeys(
  z.object({
    method: z.enum(['workos', 'email']),
    invited: z.boolean(),
  }),
);

export const project_created_schema = NoReservedKeys(
  z.object({
    project_id: z.uuid(),
    team_id: z.uuid(),
  }),
);

export const tool_opened_schema = NoReservedKeys(
  z.object({
    tool_slug: z.enum([
      'project-area',
      'n2000',
      'provincial',
      'pond-bat-checker',
      'species-checker',
    ]),
    project_id: z.uuid(),
  }),
);

export const project_opened_schema = NoReservedKeys(
  z.object({
    project_id: z.uuid(),
  }),
);

export const search_performed_schema = NoReservedKeys(
  z.object({
    query_length: z.number().int().nonnegative(),
    result_count: z.number().int().nonnegative(),
  }),
);

/**
 * Manual pageview event fired from the router on every navigation. The
 * primary reason this exists (parallel to PostHog's auto-`$pageview`) is to
 * preserve pageviews that fire BEFORE the user grants `measurement` consent
 * — the PostHog tag's machine queues them in `idle` and flushes on grant,
 * whereas the SDK's auto-`$pageview` is dropped pre-init. Post-consent it
 * also fires alongside `$pageview`; query against whichever name fits.
 *
 * `path` is the URL pathname only (no querystring, no hash) to keep search
 * terms / referrer params out of the analytics surface. UUIDs in the path
 * (`/teams/<uuid>/projects/<uuid>/...`) are already part of `project_id` /
 * `team_id` in other events — same posture.
 */
export const page_viewed_schema = NoReservedKeys(
  z.object({
    path: z.string().regex(/^\//, 'path must be a URL pathname starting with /'),
  }),
);

export const trial_dialog_opened_schema = NoReservedKeys(z.object({}));
export const trial_started_schema = NoReservedKeys(
  z.object({ has_optional_details: z.boolean() }),
);
export const trial_paused_schema = NoReservedKeys(z.object({}));
export const trial_resumed_schema = NoReservedKeys(z.object({}));
export const subscription_dialog_opened_schema = NoReservedKeys(z.object({}));
export const subscription_requested_schema = NoReservedKeys(
  z.object({ has_message: z.boolean() }),
);

export const EVENTS = {
  signup_completed: { schema: signup_completed_schema, kind: 'conversion' },
  project_created: { schema: project_created_schema, kind: 'conversion' },
  tool_opened: { schema: tool_opened_schema, kind: 'ux' },
  project_opened: { schema: project_opened_schema, kind: 'ux' },
  search_performed: { schema: search_performed_schema, kind: 'ux' },
  page_viewed: { schema: page_viewed_schema, kind: 'ux' },
  trial_dialog_opened: { schema: trial_dialog_opened_schema, kind: 'ux' },
  trial_started: { schema: trial_started_schema, kind: 'conversion' },
  trial_paused: { schema: trial_paused_schema, kind: 'ux' },
  trial_resumed: { schema: trial_resumed_schema, kind: 'ux' },
  subscription_dialog_opened: { schema: subscription_dialog_opened_schema, kind: 'ux' },
  subscription_requested: { schema: subscription_requested_schema, kind: 'conversion' },
} as const satisfies Record<string, { schema: z.ZodTypeAny; kind: EventKind }>;

export type EventName = keyof typeof EVENTS;
export type EventProps<E extends EventName> = z.infer<(typeof EVENTS)[E]['schema']>;

export function isConversionEvent(name: EventName): boolean {
  return EVENTS[name].kind === 'conversion';
}
